import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQsocialsAccessToken } from '@/lib/logto';
import type {
  CheerAggregateResponse,
  CheerAllowedResponse,
  CheerEmoji,
  CheerPostResponse,
  FollowCheckResponse,
  FollowDTO,
  FollowersListResponse,
  FollowingListResponse,
  FriendRequestsResponse,
  FriendsListResponse,
  FriendshipDTO,
  HeartbeatInput,
  OnlineFeedResponse,
  PresenceResponse,
  PublicProfileDTO,
  PublicProfileResponse,
  ResolveUsersByEmailInput,
  ResolveUsersByEmailResponse,
  ResolveUsersByEmailResult,
  ResolveUsersByExternalIdInput,
  ResolveUsersByExternalIdResponse,
  ResolveUsersByExternalIdResult,
  UpsertMyProfileInput,
  UpsertMyProfileResponse,
  UserProfileDTO,
  UserSearchResponse,
  UserSearchResult,
  WatchResponse,
} from '@/types/social';

/**
 * QSocials API client — friend/follow, presence, co-watching, cheers.
 *
 * Auth: dedicated Logto API resource `https://qsocials.qlash.gg`. Token is
 * minted on demand via the shared refresh_token (see
 * `getQsocialsAccessToken()` in src/lib/logto.ts). Same pattern as
 * qriots/qbot.
 *
 * Dev: env.qsocialsApiUrl = '/qsocials-api', Vite proxy forwards to the
 * local qsocials-api on port 3010.
 * Prod: env.qsocialsApiUrl = 'https://qsocials.qlash.gg'.
 *
 * Note: qsocials emits real-time updates via Centrifugo channels
 * (`tournament:<id>:watchers`, `match:<id>:cheers`, `presence:user:<id>`)
 * — but in dev the Centrifugo publisher is stub log-only. The hooks layer
 * (`useCentrifugoChannel`) is wired but useless until Centrifugo is wired
 * up; until then RTK Query polling is the source of truth.
 */
export const socialApi = createApi({
  reducerPath: 'socialApi',
  tagTypes: [
    'Friends',
    'FriendRequests',
    'Following',
    'Followers',
    'FollowCheck',
    'UserProfile',
    'PresenceMe',
    'OnlineFriends',
    'Watchers',
    'Cheers',
    'CheerAllowed',
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: env.qsocialsApiUrl,
    prepareHeaders: async (headers) => {
      const token = await getQsocialsAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // ---------------- User profiles (Wave 2) ----------------

    /**
     * Upsert the authenticated user's public profile (displayName + avatar).
     * Called once per session by useProfileSync from data derived off the
     * Telegram user. Idempotent server-side. Does not invalidate any list
     * tags: this writes *my* profile (how others see me), not my own lists.
     */
    upsertMyProfile: builder.mutation<UserProfileDTO, UpsertMyProfileInput>({
      query: (body) => ({
        url: '/social/me/profile',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: UpsertMyProfileResponse) => response.profile,
    }),

    /**
     * Server-side discovery search by displayName. Requires q ≥ 2 chars
     * (callers must `skip` shorter queries). Excludes the requester
     * server-side. `limit` defaults to 20 on the backend.
     */
    searchUsers: builder.query<UserSearchResult[], { q: string; limit?: number }>({
      query: ({ q, limit }) => ({
        url: '/social/users/search',
        params: { q, ...(limit != null ? { limit } : {}) },
      }),
      transformResponse: (response: UserSearchResponse) => response.results,
    }),

    /**
     * A user's public social profile by Logto `sub`: identity + the
     * relationship to the caller (friendStatus / iFollow / followsMe /
     * canMessage / mutualFriends) + counts + gated presence. Backs the
     * UserProfile screen (`/profile/:id`). Relationship-changing mutations
     * (friend/follow/block) invalidate `{ UserProfile, id }` so the screen
     * reflects the new state. 404 → user_not_found (truly unknown sub).
     */
    getUserProfile: builder.query<PublicProfileDTO, string>({
      query: (userId) => ({ url: `/social/users/${encodeURIComponent(userId)}` }),
      transformResponse: (response: PublicProfileResponse) => response.profile,
      providesTags: (_r, _e, userId) => [{ type: 'UserProfile', id: userId }],
    }),

    /**
     * Identity bridge: resolve a batch of login emails → Logto `sub`.
     * Server-side resolver (Logto Management API, cached). Emails with no
     * Logto match are omitted from `results`. The caller builds an
     * email → sub map to light up <PlayerSocialButtons> for participants
     * that resolve. A query (not a mutation) so RTK Query caches the batch
     * per email-set and dedupes re-renders of the participants list.
     */
    resolveUsersByEmail: builder.query<
      ResolveUsersByEmailResult[],
      ResolveUsersByEmailInput
    >({
      query: (body) => ({
        url: '/social/users/resolve-by-email',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ResolveUsersByEmailResponse) =>
        response.results,
    }),

    /**
     * Deterministic identity bridge: resolve a batch of Laravel `users.id`
     * (digits-only) → Logto `sub`. Each user self-publishes their own
     * `externalUserId` on launch (useProfileSync), so this lights up
     * <PlayerSocialButtons> even for participants whose login email doesn't
     * match Logto (Apple "Hide My Email", social login). Ids with no published
     * profile are omitted from `results`. Preferred over resolve-by-email;
     * email is only the fallback. Cap ≤ 100 ids per call. A query (not a
     * mutation) so RTK Query caches the batch per id-set.
     */
    resolveUsersByExternalId: builder.query<
      ResolveUsersByExternalIdResult[],
      ResolveUsersByExternalIdInput
    >({
      query: (body) => ({
        url: '/social/users/resolve-by-external-id',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ResolveUsersByExternalIdResponse) =>
        response.results,
    }),

    // ---------------- Friendships ----------------

    listFriends: builder.query<FriendshipDTO[], void>({
      query: () => ({ url: '/social/friends' }),
      transformResponse: (response: FriendsListResponse) => response.friends,
      providesTags: ['Friends'],
    }),

    listFriendRequests: builder.query<
      { incoming: FriendshipDTO[]; outgoing: FriendshipDTO[] },
      void
    >({
      query: () => ({ url: '/social/friends/requests' }),
      transformResponse: (response: FriendRequestsResponse) => ({
        incoming: response.incoming,
        outgoing: response.outgoing,
      }),
      providesTags: ['FriendRequests'],
    }),

    requestFriendship: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/request',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
      ],
    }),

    acceptFriendship: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/accept',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
      ],
    }),

    removeFriendship: builder.mutation<void, { friendUserId: string }>({
      query: ({ friendUserId }) => ({
        url: `/social/friends/${encodeURIComponent(friendUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
      ],
    }),

    blockUser: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/block',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        'Following',
        'Followers',
        { type: 'UserProfile', id: friendUserId },
        { type: 'FollowCheck', id: friendUserId },
      ],
    }),

    unblockUser: builder.mutation<void, { friendUserId: string }>({
      query: ({ friendUserId }) => ({
        url: `/social/friends/block/${encodeURIComponent(friendUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
      ],
    }),

    // ---------------- Follows ----------------

    listFollowing: builder.query<FollowDTO[], void>({
      query: () => ({ url: '/social/follows/following' }),
      transformResponse: (response: FollowingListResponse) => response.following,
      providesTags: ['Following'],
    }),

    listFollowers: builder.query<FollowDTO[], void>({
      query: () => ({ url: '/social/follows/followers' }),
      transformResponse: (response: FollowersListResponse) => response.followers,
      providesTags: ['Followers'],
    }),

    checkFollow: builder.query<
      { iFollow: boolean; followsMe: boolean },
      string
    >({
      query: (userId) => ({
        url: `/social/follows/check/${encodeURIComponent(userId)}`,
      }),
      transformResponse: (response: FollowCheckResponse) => ({
        iFollow: response.iFollow,
        followsMe: response.followsMe,
      }),
      providesTags: (_r, _e, userId) => [{ type: 'FollowCheck', id: userId }],
    }),

    followUser: builder.mutation<void, { followedUserId: string }>({
      query: (body) => ({
        url: '/social/follows',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { followedUserId }) => [
        'Following',
        { type: 'FollowCheck', id: followedUserId },
        { type: 'UserProfile', id: followedUserId },
      ],
    }),

    unfollowUser: builder.mutation<void, { followedUserId: string }>({
      query: ({ followedUserId }) => ({
        url: `/social/follows/${encodeURIComponent(followedUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { followedUserId }) => [
        'Following',
        { type: 'FollowCheck', id: followedUserId },
        { type: 'UserProfile', id: followedUserId },
      ],
    }),

    // ---------------- Presence ----------------

    heartbeat: builder.mutation<PresenceResponse, HeartbeatInput>({
      query: (body) => ({
        url: '/social/presence/heartbeat',
        method: 'POST',
        body,
      }),
      // Heartbeat does not invalidate anything by default — it's a high-frequency
      // mutation. Online-friends feed is polled and will reflect changes
      // naturally; invalidating would cause needless refetches.
    }),

    goOffline: builder.mutation<void, void>({
      query: () => ({
        url: '/social/presence/offline',
        method: 'POST',
      }),
      invalidatesTags: ['PresenceMe', 'OnlineFriends'],
    }),

    getMyPresence: builder.query<PresenceResponse, void>({
      query: () => ({ url: '/social/presence/me' }),
      providesTags: ['PresenceMe'],
    }),

    getOnlineFriendsFeed: builder.query<OnlineFeedResponse, void>({
      query: () => ({ url: '/social/presence/feed/online-friends' }),
      providesTags: ['OnlineFriends'],
    }),

    getUserPresence: builder.query<PresenceResponse, string>({
      query: (userId) => ({
        url: `/social/presence/${encodeURIComponent(userId)}`,
      }),
    }),

    // ---------------- Watch (co-watching) ----------------

    joinWatch: builder.mutation<WatchResponse, { tournamentId: string }>({
      query: ({ tournamentId }) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { tournamentId }) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    leaveWatch: builder.mutation<WatchResponse, { tournamentId: string }>({
      query: ({ tournamentId }) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { tournamentId }) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    getWatchers: builder.query<WatchResponse, string>({
      query: (tournamentId) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
      }),
      providesTags: (_r, _e, tournamentId) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    // ---------------- Cheers ----------------

    getAllowedCheers: builder.query<readonly string[], void>({
      query: () => ({ url: '/social/cheers/allowed' }),
      transformResponse: (response: CheerAllowedResponse) => response.allowed,
      providesTags: ['CheerAllowed'],
      // Allowed list rarely changes. Keep cached for the whole session.
      keepUnusedDataFor: 86400,
    }),

    postCheer: builder.mutation<
      CheerPostResponse,
      { matchId: string; emoji: CheerEmoji | string }
    >({
      query: (body) => ({
        url: '/social/cheers',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { matchId }) => [
        { type: 'Cheers', id: matchId },
      ],
    }),

    getCheers: builder.query<CheerAggregateResponse, string>({
      query: (matchId) => ({
        url: `/social/cheers/${encodeURIComponent(matchId)}`,
      }),
      providesTags: (_r, _e, matchId) => [{ type: 'Cheers', id: matchId }],
    }),
  }),
});

export const {
  useUpsertMyProfileMutation,
  useSearchUsersQuery,
  useGetUserProfileQuery,
  useResolveUsersByEmailQuery,
  useResolveUsersByExternalIdQuery,
  useListFriendsQuery,
  useListFriendRequestsQuery,
  useRequestFriendshipMutation,
  useAcceptFriendshipMutation,
  useRemoveFriendshipMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useListFollowingQuery,
  useListFollowersQuery,
  useCheckFollowQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useHeartbeatMutation,
  useGoOfflineMutation,
  useGetMyPresenceQuery,
  useGetOnlineFriendsFeedQuery,
  useGetUserPresenceQuery,
  useJoinWatchMutation,
  useLeaveWatchMutation,
  useGetWatchersQuery,
  useGetAllowedCheersQuery,
  usePostCheerMutation,
  useGetCheersQuery,
} = socialApi;
